import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

import { AuthShell } from "@/app/auth/AuthShell";
import { FormRootError, TextField } from "@/components/app/form-field";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { api, applyServerErrors, toApiError } from "@/lib/api";

const schema = z
  .object({
    new_password: z.string().min(10, "auth.passwordTooShort"),
    confirm_password: z.string().min(1, "auth.passwordRequired"),
  })
  .refine((values) => values.new_password === values.confirm_password, {
    path: ["confirm_password"],
    message: "auth.passwordMismatch",
  });

type FormValues = z.infer<typeof schema>;

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const uid = searchParams.get("uid") ?? "";
  const token = searchParams.get("token") ?? "";
  const linkValid = Boolean(uid && token);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { new_password: "", confirm_password: "" },
  });

  const onSubmit = async (values: FormValues) => {
    try {
      await api.post("auth/password/reset/confirm/", {
        uid,
        token,
        new_password: values.new_password,
      });
      toast.success(t("auth.resetSuccess"));
      navigate("/login", { replace: true });
    } catch (error) {
      const apiError = toApiError(error);
      if (!applyServerErrors(form, apiError)) {
        form.setError("root", { message: apiError.detail || t("auth.resetInvalidLink") });
      }
    }
  };

  return (
    <AuthShell
      title={t("auth.resetTitle")}
      description={t("auth.resetSubtitle")}
      footer={
        <Link
          to="/login"
          className="text-muted-foreground hover:text-foreground underline underline-offset-4"
        >
          {t("auth.backToLogin")}
        </Link>
      }
    >
      {linkValid ? (
        <Form {...form}>
          <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)} noValidate>
            <FormRootError message={form.formState.errors.root?.message} />
            <TextField
              control={form.control}
              name="new_password"
              type="password"
              autoComplete="new-password"
              label={t("auth.newPassword")}
              required
            />
            <TextField
              control={form.control}
              name="confirm_password"
              type="password"
              autoComplete="new-password"
              label={t("auth.confirmPassword")}
              required
            />
            <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? t("action.saving") : t("auth.resetSubmit")}
            </Button>
          </form>
        </Form>
      ) : (
        <p role="alert" className="text-destructive text-sm">
          {t("auth.resetInvalidLink")}
        </p>
      )}
    </AuthShell>
  );
}
